import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Copy, Check, Download, Shield, AlertTriangle } from "lucide-react";

interface BackupCodesDisplayProps {
    backupCodes: string[];
    onContinue: () => void;
}

export const BackupCodesDisplay: React.FC<BackupCodesDisplayProps> = ({ backupCodes, onContinue }) => {
    const [copied, setCopied] = useState(false);
    const [downloaded, setDownloaded] = useState(false);

    const copyToClipboard = async () => {
        const codesText = backupCodes.join("\n");
        await navigator.clipboard.writeText(codesText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const downloadCodes = () => {
        const codesText = backupCodes.join("\n");
        const blob = new Blob([codesText], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "backup-codes.txt";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setDownloaded(true);
    };

    return (
        <Card className="mx-auto w-full max-w-md">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-green-600" />
                    Two-Factor Authentication Enabled!
                </CardTitle>
                <CardDescription>
                    Save these backup codes in a safe place. You can use them to access your account if you lose your
                    authenticator device.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        <strong>Important:</strong> Each backup code can only be used once. Store them securely and
                        don't share them with anyone.
                    </AlertDescription>
                </Alert>

                {/* Backup Codes Grid */}
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-900">
                    <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                        {backupCodes.map((code, index) => (
                            <div key={index} className="rounded border bg-white p-2 text-center dark:bg-gray-800">
                                {code}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={copyToClipboard} className="flex-1">
                        {copied ? (
                            <>
                                <Check className="mr-2 h-4 w-4" />
                                Copied!
                            </>
                        ) : (
                            <>
                                <Copy className="mr-2 h-4 w-4" />
                                Copy
                            </>
                        )}
                    </Button>
                    <Button type="button" variant="outline" onClick={downloadCodes} className="flex-1">
                        <Download className="mr-2 h-4 w-4" />
                        {downloaded ? "Downloaded!" : "Download"}
                    </Button>
                </div>

                {/* Continue Button */}
                <Button onClick={onContinue} className="w-full" disabled={!copied && !downloaded}>
                    {copied || downloaded ? "Continue to Dashboard" : "Save codes first"}
                </Button>

                {!copied && !downloaded && (
                    <p className="text-center text-sm text-muted-foreground">
                        Please copy or download your backup codes before continuing
                    </p>
                )}
            </CardContent>
        </Card>
    );
};
